<template>
  <div v-if="indexedId" :key="indexedId" class="p-actor">
    <TitleHeader :heading="pageTitle" :subhead="actorLabel" />
    <div
      v-show="hasDescription || actor?.metadata"
      class="p-actor__details"
      :class="{
        'p-actor__details--two-column': description && actor
      }"
    >
      <RichText
        v-if="hasDescription"
        :content="description ? description : ulanDescription"
        :class="[
          { ['p-actor__ulan-description']: ulanDescription },
          'p-actor__description'
        ]"
      />
      <div class="p-actor__metadata">
        <ConstituentMetadata v-if="actor" :actor="actor" />
      </div>
    </div>
    <RichText
      v-if="usesUlanData"
      class="p-actor__notes"
      :content="ulanLabel"
      :content-type="'markdown'"
    />
    <ConstituentRelatedObjects
      v-if="actor"
      :actor="actor"
      :constituent-indexed-id="indexedId"
      :show-owned-works="!isGetty"
    />
    <div class="p-actor__related-actors">
      <RelatedSearchResults
        v-if="relatedActors"
        :aria-heading="relatedActorsHeading"
        image-unavailable-variant="actor"
        :heading-level="2"
        :heading="relatedActorsHeading"
        :items="relatedActors.data"
        :total="relatedActors.total"
        :use-route="false"
      />
    </div>
    <TechnicalData :metadata-fields="technicalMetadataFields" />
  </div>
</template>

<script>
import {
  RichText,
  TechnicalData,
  useLocalization,
  useEventBus
} from "@thegetty-private/getty-ui";
import ConstituentMetadata from "@/components/molecules/constituentMetadata/index.vue";
import ConstituentRelatedObjects from "@/components/molecules/constituentRelatedObjects/index.vue";
import TitleHeader from "@/components/atoms/titleHeader/index.vue";
import RelatedSearchResults from "@/components/molecules/relatedSearchResults/index.vue";
import { mapStores } from "pinia";
import { useActorStore } from "@/stores/actor.js";
import { useHead } from "@unhead/vue";
import { ref } from "vue";

const title = ref(null);
const metaInfo = ref(null);

export default {
  name: "PersonHome",
  components: {
    ConstituentMetadata,
    ConstituentRelatedObjects,
    RelatedSearchResults,
    RichText,
    TechnicalData,
    TitleHeader
  },
  inject: ["updatePageTitle"],
  props: {
    /**
     * The indexed ID of the record to load
     */
    indexedId: {
      type: String,
      default: undefined
    },
    /**
     * a way to inject a static object into the page
     */
    storybookData: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus()
    };
  },
  computed: {
    ...mapStores(useActorStore),
    /**
     * The transformed actor record from the store
     * @returns {object}
     */
    actor() {
      if (this.storybookData != undefined) {
        return this.storybookData;
      }
      return this.actorStore.getActor(this.indexedId);
    },
    /**
     * The actor's description
     * @returns {string}
     */
    description() {
      return this.actor?.description;
    },
    /**
     * Returns the ulanDescription or undefined if we're using the LOD
     * description.
     * @returns {string | undefined}
     */
    ulanDescription() {
      if (this.description) {
        return undefined;
      }
      return this.actor?.ulan?.ulanDescription;
    },
    /**
     * Whether we have a description from the LOD or ULAN
     * @returns {boolean}
     */
    hasDescription() {
      return this.description || this.ulanDescription;
    },
    /**
     * Whether we using a ULAN description or ULAN metadata
     * @returns {boolean}
     */
    usesUlanData() {
      if (this.actor?.metadata) {
        return (
          this.ulanDescription ||
          Object.keys(this.actor?.metadata).some((key) => key.includes("ulan"))
        );
      }
      return false;
    },
    /**
     * The actor name / page title
     * @returns {string}
     */
    pageTitle() {
      return this.actor?.pageTitle;
    },
    /**
     * The type label subheading
     * @returns {string}
     */
    actorLabel() {
      if (!this.actor) {
        return;
      }

      let type = this.actor?.type;
      return type != undefined
        ? this.translate(`actor_page.${type}`)
        : undefined;
    },
    /**
     * Label for ULAN Note
     * @returns {string}
     */
    ulanLabel() {
      let text = this.translate("actor_page.ulan_link");
      let linkText = `[${text}](${this.actor?.ulan?.link})`;
      return this.translatePart("actor_page.note", { link: linkText });
    },
    /**
     * The related actors from ULAN
     */
    relatedActors() {
      return this.actor?.ulan?.relatedActors;
    },
    /**
     * The heading for the related actors section
     */
    relatedActorsHeading() {
      return this.translatePart("actor_page.related_actors_heading");
    },
    /**
     * The metadata fields for the technical data section for the actor
     * @returns {object}
     */
    technicalMetadataFields() {
      return this.actor?.technicalMetadata;
    },
    /**
     * Checks whether the current actor is Getty/Center/Villa
     * @returns {boolean}
     */
    isGetty() {
      return this.actor.isGetty;
    }
  },
  watch: {
    indexedId: "fetchData"
  },
  async created() {
    await this.fetchData();
  },
  mounted() {
    useHead({
      title: title,
      meta: metaInfo
    });
  },

  methods: {
    /**
     * Triggers the store to fetch & create actor object/data
     */
    async fetchData() {
      if (this.indexedId == undefined || this.storybookData != undefined) {
        return;
      }
      await this.actorStore.addActor(this.indexedId, this.translatePart);

      this.updatePageTitle(this.pageTitle);
      title.value = this.actor?.head?.title;
      metaInfo.value = this.actor?.head?.metadata;

      // check for metadata to send for analytics
      this.eventBus.emit("PageMetadata", {
        tmsId: this.actor?.technicalMetadata?.other_ids?.tms_id?.name
      });

      // data for sticky header
      this.eventBus.emit("data-ready", {
        heading: this.pageTitle,
        subheading: this.actor?.subheading
      });
    }
  }
};
</script>
<style lang="scss">
@import "./index";
</style>
